import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { SeoTags } from '~/components/seo-tags'
import { buttonVariants } from '~/components/ui/button'
import { DistrictsSpaceCarousel } from '~/domains/spaces/components/molecules/districts-space-carousel'
import { useTracking } from '~/providers/tracking-provider'

const IMG_REFS: Record<string, string> = {
	paris: '/images/v2/navbar/paris.svg',
	lyon: '/images/v2/navbar/lyon.svg',
}

const LANDING_URL_REFS: Record<string, string> = {
	hourlyOffices: 'bureau-a-l-heure',
	meetingRooms: 'salle-de-reunion',
}

export interface LandingZonePageProps {
	landingPrefix: 'hourlyOffices' | 'meetingRooms'
}

export const LandingZonePage = ({ landingPrefix }: LandingZonePageProps) => {
	const { query } = useRouter()
	const { t } = useTranslation()
	const { track } = useTracking()

	const zone = query.zone as Zone

	const jsonLD = useMemo(
		() => ({
			'@context': 'https://schema.org',
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: t(`cwLandings:${landingPrefix}.metaTitle`, { zone }),
					item: `https://www.cocoon-space.com/${LANDING_URL_REFS[landingPrefix]}/${zone}`,
				},
			],
		}),
		[t, landingPrefix],
	)

	return (
		<div className='container flex flex-col items-start gap-4 py-12'>
			<SeoTags
				title={t(`cwLandings:${landingPrefix}.metaTitle`, { zone })}
				description={t(`cwCommon:seo.metaDescription.${zone}`)}
			>
				<script
					dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLD) }}
					type='application/ld+json'
				/>
			</SeoTags>

			<header>
				{IMG_REFS[zone] && (
					<Image
						alt={zone}
						src={IMG_REFS[zone]}
						height={60}
						width={60}
						className='mb-2'
					/>
				)}

				<h1 className='font-bold text-4xl'>
					{t(`cwLandings:${landingPrefix}.title`, { zone })}
				</h1>

				<div className='max-w-[800px] text-lg'>
					{t(`cwLandings:${landingPrefix}.baseline`, { zone })}
				</div>
			</header>

			<div className='max-w-[800px]'>
				{t(`cwLandings:${landingPrefix}.${zone}.description`)}
			</div>

			<Link
				href={`/${zone}`}
				className={buttonVariants({
					variant: 'gradient',
					className: 'my-4 mb-1 text-center',
				})}
				onClick={() => track.click(`landing-${landingPrefix}-seeAllCocoonBtn`)}
			>
				{t('cwDistricts:seeAllCocoonInBtn', { zone })}
			</Link>

			<DistrictsSpaceCarousel zone={zone} />
		</div>
	)
}
